/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { buildReturnSearch, buildReturnUrl } from 'utils/return-url';
import {
  hasHandedOffToAccounts,
  redirectToAccountsLogin,
} from 'utils/accounts';

import ScreenLoading from 'views/ui/screen-loading';
import { isKnownPath } from 'routes/known-paths';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { verifySession } from 'modules/auth/actions';

// Hands an unauthenticated visitor to the shared accounts app, carrying the page they
// were trying to reach as the `return` param — so typing /app/user-manager while signed
// out lands on the accounts login screen and comes back to /app/user-manager afterwards.
//
// The redirect runs from an effect rather than inline in render because it mutates
// window.location; render itself stays pure.
const AccountsLoginRedirect = ({ location }) => {
  // Read on mount only: whether this tab has already been sent to the accounts app.
  // Coming back here after that means the two apps disagree about the session, and
  // handing off a second time is what makes the browser bounce between them forever.
  const [alreadyTried] = useState(hasHandedOffToAccounts);
  const [unreachable, setUnreachable] = useState(false);

  useEffect(() => {
    if (alreadyTried) {
      toast.error('We could not verify your session. Please sign in again.');
      return;
    }

    try {
      redirectToAccountsLogin(buildReturnUrl(location));
    } catch (error) {
      // A misconfigured REACT_APP_ACCOUNTS_URL throws instead of stranding the user on a
      // rewritten pathname. Fall back to the in-app login screen, keeping the `return`
      // value so the deep link still resolves — same handling as sessionExpiryWatcherSaga.
      toast.error('Could not reach the sign-in page. Please sign in again.');
      setUnreachable(true);
    }
  }, [alreadyTried, location]);

  if (alreadyTried || unreachable) {
    return (
      <Redirect to={{ pathname: '/', search: buildReturnSearch(location) }} />
    );
  }

  // The navigation is already under way; hold the loading screen rather than flashing
  // the private screen or a blank page.
  return <ScreenLoading />;
};

const PrivateRoute = ({
  render,
  isLoggedIn,
  sessionChecked,
  me,
  requiredRole,
  ...rest
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(verifySession());
  }, [dispatch]);

  const isRoleDenied =
    requiredRole && me && me.role && me.role.name !== requiredRole;

  return (
    <Route
      {...rest}
      render={(otherProps) => {
        if (!isLoggedIn) {
          // Wait for the verifySession dispatched above to answer. `isLoggedIn` is false
          // before it does, and handing off on that would send a user with a perfectly
          // good session out to the accounts app, which would only bounce them back.
          if (!sessionChecked) return <ScreenLoading />;

          // /app matches any path beneath it, so a URL like /app/library gets this far
          // without naming a real screen. Nobody should be sent to another app to sign
          // in for a page that does not exist: show the login screen and drop the
          // pointless return value.
          if (!isKnownPath(otherProps.location.pathname)) {
            return <Redirect to="/" />;
          }

          return <AccountsLoginRedirect location={otherProps.location} />;
        }
        if (isRoleDenied) {
          return <Redirect to="/app" />;
        }
        return render(otherProps);
      }}
    />
  );
};

export default PrivateRoute;
